import { useEffect, useState } from "react";
import { useParams, useLocation, Link } from "react-router-dom";
import { SlidersHorizontal, Home as HomeIcon, FileText, ExternalLink, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Seo, { SITE_URL } from "@/components/Seo";

const VALVES_BROCHURE = { label: "Myson Valves Brochure", url: "https://blobfiles.purmogroup.app/public/296640/1535293" };
const BROCHURES = {
  "myson-trvs": VALVES_BROCHURE,
  "myson-manual-radiator-valves": VALVES_BROCHURE,
  "myson-mpe-valves": VALVES_BROCHURE,
  "myson-thermostats": { label: "Myson Electronic Controls Brochure", url: "https://www.myson.co.uk/downloads/brochures-and-catalogues/" },
};

const CATEGORY_IMAGES = {
  "myson-trvs": { src: "/trv-category.jpg", alt: "Genuine Myson thermostatic radiator valve fitted to a white radiator in a modern living room" },
  "myson-manual-radiator-valves": { src: "/manual-category.jpg", alt: "Myson manual radiator valve fitted to a white horizontal radiator in a modern room" },
  "myson-thermostats": { src: "/controls-category.jpg", alt: "Myson electronic programmable room thermostat displaying 22.5°C on a wooden surface" },
  "kickspace-grills": { src: "/fan-category.jpg", alt: "Myson Kickspace plinth fan convector grille fitted beneath a kitchen unit" },
  "myson-mpe-valves": { src: "/motorised-category.jpg", alt: "Motorised zone valve actuator fitted to copper pipework in a heating system" },
};

const ATTR_FILTERS = {
  "myson-trvs": [
    { label: "10mm", test: (p) => /10mm/i.test(p.name) },
    { label: "15mm", test: (p) => /15mm/i.test(p.name) },
    { label: "Angled", test: (p) => /angle/i.test(p.name) },
    { label: "Pushfit", test: (p) => /push ?fit/i.test(p.name) },
    { label: "TRV + Lockshield Packs", test: (p) => /pack/i.test(p.name) },
  ],
  "myson-manual-radiator-valves": [
    { label: "10mm", test: (p) => /10mm/i.test(p.name) },
    { label: "15mm", test: (p) => /15mm/i.test(p.name) },
    { label: "Wheelhead", test: (p) => /wheelhead/i.test(p.name) },
    { label: "Lockshield", test: (p) => /lockshield/i.test(p.name) },
    { label: "Drain-off", test: (p) => /drain ?-?off/i.test(p.name) },
    { label: "Pushfit", test: (p) => /push ?fit/i.test(p.name) },
  ],
  "myson-mpe-valves": [
    { label: "2 Port", test: (p) => /2 ?-?port/i.test(p.name) },
    { label: "3 Port", test: (p) => /3 ?-?port/i.test(p.name) },
    { label: "22mm", test: (p) => /22mm/i.test(p.name) },
    { label: "28mm", test: (p) => /28mm/i.test(p.name) },
  ],
  "myson-thermostats": [
    { label: "Wireless / RF", test: (p) => /\bRF\b|wireless/i.test(`${p.name} ${p.description || ""}`) },
    { label: "Programmable", test: (p) => /programmab|programmer/i.test(`${p.name} ${p.description || ""}`) },
    { label: "OpenTherm", test: (p) => /opentherm/i.test(`${p.name} ${p.description || ""}`) },
    { label: "Smart / WiFi", test: (p) => /wifi|smart|app/i.test(`${p.name} ${p.description || ""}`) },
    { label: "Heating + Hot Water", test: (p) => /hot water|2 ?-?channel|twin channel|dual channel|cylinder/i.test(`${p.name} ${p.description || ""}`) },
  ],
};

const COLLECTION_SEO = {
  "myson-trvs": {
    meta: "Buy genuine Myson TRV2 thermostatic radiator valves and valve packs in the UK. A-rated 10mm & 15mm TRVs with liquid sensors and pushfit options — free tracked UK delivery.",
    intro: "Shop our full range of Myson TRV2 thermostatic radiator valves (TRVs) for precise, energy-saving room-by-room heating control. Every valve is a genuine Myson part with an A-rated liquid sensor, bi-directional flow and frost protection, available in 10mm and 15mm sizes plus complete radiator packs and pushfit elbow options. All orders ship free across mainland UK.",
  },
  "myson-manual-radiator-valves": {
    meta: "Genuine Myson manual radiator valves and lockshields in chrome and white. Angled and straight wheelhead valves for UK central heating — free tracked UK delivery.",
    intro: "Browse Myson manual radiator valves and matching lockshields for reliable on/off control of your radiators. Available as angled or straight wheelhead valves in chrome and white finishes to suit any UK central heating installation, all supplied as genuine Myson parts with free tracked UK delivery.",
  },
  "myson-thermostats": {
    h1: "Thermostats & Heating Programmers",
    overline: "Electronic Controls",
    meta: "Room thermostats, programmers & smart heating controls from Myson, Danfoss, Drayton & EPH. Wired and wireless RF thermostats with OpenTherm — free tracked UK delivery.",
    intro: "Discover our range of electronic heating controls, including room thermostats, programmers, cylinder thermostats and smart wireless (RF) controls from leading brands such as Myson, Danfoss, Drayton and EPH. From simple dial stats to OpenTherm-compatible programmable and app-controlled models, you'll find the right control for combi and system boilers — with free tracked UK delivery on every order.",
  },
  "myson-mpe-valves": {
    meta: "Myson MPE motorised zone valves for UK central heating and hot water systems. Dependable actuator valves for S-plan and Y-plan — genuine parts, free tracked UK delivery.",
    intro: "Shop Myson MPE motorised zone valves for controlling heating and hot water zones in UK central heating systems. These genuine Myson actuator valves provide dependable automatic zone control for S-plan and Y-plan installations, all with free tracked UK delivery.",
  },
  "kickspace-grills": {
    meta: "Myson Kickspace plinth fan convector heaters and replacement grilles. Hydronic, electric & dual-fuel kickspace heaters for kitchens — free tracked UK delivery.",
    intro: "Explore Myson Kickspace fan convectors and plinth heaters — space-saving warmth that fits neatly under kitchen units and in hard-to-heat rooms. Choose from hydronic (hot water), electric and dual-fuel Kickspace 500 models, plus genuine replacement grilles in white, black, chrome and stainless steel. Free tracked UK delivery on all orders.",
  },
  "bleed-vents": {
    meta: "Radiator bleed vents, bleed plugs and touch-up paint. Genuine Myson spares and heating accessories for radiators — free tracked UK delivery.",
    intro: "Find genuine Myson heating accessories and spare parts, including radiator bleed vents, bleed plugs and colour-matched touch-up paint. Keep your radiators and heating system in top condition with authentic Myson replacements — all with free tracked UK delivery.",
  },
};

export default function Collection() {
  const { slug } = useParams();
  const loc = useLocation();
  const searchQ = new URLSearchParams(loc.search).get("q") || "";

  const [collection, setCollection] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("");
  const [inStock, setInStock] = useState(false);
  const [maxPrice, setMaxPrice] = useState(500);
  const [brand, setBrand] = useState("");
  const [brandOptions, setBrandOptions] = useState([]);
  const [attrs, setAttrs] = useState([]);

  const isSearch = !slug;
  const attrFilters = !isSearch ? (ATTR_FILTERS[slug] || []) : [];
  const toggleAttr = (label) => setAttrs((a) => (a.includes(label) ? a.filter((x) => x !== label) : [...a, label]));
  const shown = attrs.length
    ? products.filter((p) => attrFilters.filter((f) => attrs.includes(f.label)).every((f) => f.test(p)))
    : products;

  useEffect(() => { setAttrs([]); }, [slug]);

  useEffect(() => {
    setLoading(true);
    setBrand("");
    if (!isSearch) {
      api.get(`/collections/${slug}`).then((r) => setCollection(r.data)).catch(() => setCollection(null));
    } else {
      setCollection(null);
    }
  }, [slug, isSearch]);

  useEffect(() => {
    setLoading(true);
    const params = {};
    if (!isSearch) params.collection = slug;
    if (isSearch && searchQ) params.search = searchQ;
    if (sort) params.sort = sort;
    if (inStock) params.in_stock = true;
    if (maxPrice < 150) params.max_price = maxPrice;
    if (brand) params.brand = brand;
    api.get("/products", { params })
      .then((r) => {
        setProducts(r.data);
        if (!brand) setBrandOptions([...new Set(r.data.map((p) => p.brand).filter(Boolean))].sort());
      })
      .finally(() => setLoading(false));
  }, [slug, searchQ, sort, inStock, maxPrice, brand, isSearch]);

  const title = isSearch ? `Search results for "${searchQ}"` : collection?.name || "Products";
  const brochure = !isSearch ? BROCHURES[slug] : null;

  const collectionJsonLd = (!isSearch && collection) ? (() => {
    const breadcrumb = {
      "@context": "https://schema.org/",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
        { "@type": "ListItem", position: 2, name: collection.name, item: `${SITE_URL}/collections/${slug}` },
      ],
    };
    const itemList = {
      "@context": "https://schema.org/",
      "@type": "ItemList",
      name: collection.name,
      itemListElement: products.map((p, i) => ({
        "@type": "ListItem",
        position: i + 1,
        item: {
          "@type": "Product",
          name: p.name,
          url: `${SITE_URL}/products/${p.slug}`,
          image: p.image,
          sku: p.sku,
          brand: { "@type": "Brand", name: p.brand || "Myson" },
          offers: {
            "@type": "Offer",
            priceCurrency: "GBP",
            price: p.price,
            availability: p.stock === 0 ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
            url: `${SITE_URL}/products/${p.slug}`,
          },
          ...(p.review_summary?.count > 0 && {
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: p.review_summary.average,
              reviewCount: p.review_summary.count,
              bestRating: 5,
              worstRating: 1,
            },
          }),
        },
      })),
    };
    return products.length > 0 ? [breadcrumb, itemList] : breadcrumb;
  })() : undefined;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <Seo
        title={isSearch ? `Search: ${searchQ}` : (COLLECTION_SEO[slug]?.h1 || collection?.name)}
        description={isSearch ? undefined : (COLLECTION_SEO[slug]?.meta || collection?.description)}
        noindex={isSearch}
        jsonLd={collectionJsonLd}
      />
      <nav className="flex items-center gap-1.5 text-xs text-muted-foreground mb-6">
        <Link to="/" className="hover:text-primary flex items-center gap-1"><HomeIcon className="w-3.5 h-3.5" /> Home</Link>
        <span>/</span>
        <span className="text-foreground font-medium">{title}</span>
      </nav>

      {!isSearch && collection && (
        <div className={`mb-8 ${CATEGORY_IMAGES[slug] ? "grid lg:grid-cols-[1fr_minmax(0,480px)] gap-8 lg:gap-10 items-center" : ""}`}>
          <div>
            <span className="text-overline text-primary">{COLLECTION_SEO[slug]?.overline || collection.tagline}</span>
            <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-display font-black tracking-tighter">{COLLECTION_SEO[slug]?.h1 || collection.name}</h1>
            <p className="mt-3 text-muted-foreground max-w-2xl leading-relaxed">{collection.description}</p>
            {COLLECTION_SEO[slug]?.intro && (
              <p className="mt-3 text-sm text-muted-foreground max-w-3xl leading-relaxed" data-testid="collection-intro">{COLLECTION_SEO[slug].intro}</p>
            )}
            {brochure && (
              <a href={brochure.url} target="_blank" rel="noopener noreferrer" data-testid="brochure-link"
                className="mt-4 inline-flex items-center gap-2 border border-border bg-white px-4 py-2.5 rounded-sm text-sm font-semibold hover:border-primary transition-colors">
                <FileText className="w-4 h-4 text-primary" /> {brochure.label}
                <ExternalLink className="w-3.5 h-3.5 text-muted-foreground" />
              </a>
            )}
            {slug === "kickspace-grills" && (
              <Link to="/guides/myson-fan-convector-technical-data" data-testid="technical-data-link"
                className="mt-4 ml-0 sm:ml-3 inline-flex items-center gap-2 border border-border bg-white px-4 py-2.5 rounded-sm text-sm font-semibold hover:border-primary transition-colors">
                <FileText className="w-4 h-4 text-primary" /> Technical data (heat outputs &amp; dimensions)
              </Link>
            )}
          </div>
          {CATEGORY_IMAGES[slug] && (
            <img
              src={CATEGORY_IMAGES[slug].src}
              alt={CATEGORY_IMAGES[slug].alt}
              loading="eager"
              data-testid="category-hero-image"
              className="hidden sm:block w-full rounded-lg border border-border shadow-lg shadow-black/5 ring-1 ring-black/5 object-cover"
            />
          )}
        </div>
      )}
      {isSearch && (
        <h1 className="mb-8 text-2xl sm:text-3xl font-display font-bold tracking-tight" data-testid="search-title">{title}</h1>
      )}

      {slug === "bargain-bin" && (
        <div className="mb-8 rounded-sm border border-amber-300 bg-amber-50 p-5 sm:p-6" data-testid="bargain-notice">
          <p className="text-sm sm:text-base text-amber-900 leading-relaxed">
            Every item here is <span className="font-bold bg-amber-200 px-1.5 py-0.5 rounded-sm">new stock</span> with <span className="font-bold bg-amber-200 px-1.5 py-0.5 rounded-sm">damaged packaging</span> — never used, with our 30-day returns policy.
          </p>
          <p className="mt-3 text-xs sm:text-sm font-semibold text-amber-900">
            Damaged packaging alone isn't a fault, but if the product itself is faulty you have exactly the same rights as with any other purchase.
          </p>
        </div>
      )}

      <div className="grid lg:grid-cols-[240px_1fr] gap-8">
        {/* FILTERS */}
        <aside className="space-y-6">
          <div className="flex items-center gap-2 font-semibold"><SlidersHorizontal className="w-4 h-4 text-primary" /> Filters</div>
          <div className="bg-card border border-border rounded-sm p-4 space-y-5">
            {brandOptions.length > 1 && (
              <div>
                <label className="text-sm font-medium">Brand</label>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="brand-filter">
                  <button onClick={() => setBrand("")} data-testid="brand-all"
                    className={`px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors ${brand === "" ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-muted"}`}>
                    All
                  </button>
                  {brandOptions.map((b) => (
                    <button key={b} onClick={() => setBrand(b)} data-testid={`brand-${b.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors ${brand === b ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-muted"}`}>
                      {b}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className="text-sm font-medium">Max price: £{maxPrice}</label>
              <input type="range" min="5" max="500" value={maxPrice} data-testid="price-filter"
                onChange={(e) => setMaxPrice(Number(e.target.value))}
                className="w-full mt-2 accent-primary" />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="in-stock-filter">
              <input type="checkbox" checked={inStock} onChange={(e) => setInStock(e.target.checked)} className="accent-primary w-4 h-4" />
              In stock only
            </label>
            {attrFilters.length > 0 && (
              <div className="pt-1 border-t border-border">
                <label className="text-sm font-medium">Refine by type</label>
                <div className="mt-2 flex flex-wrap gap-2" data-testid="attr-filter">
                  {attrFilters.map((f) => (
                    <button key={f.label} onClick={() => toggleAttr(f.label)}
                      data-testid={`attr-${f.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}
                      className={`px-3 py-1.5 rounded-sm text-xs font-medium border transition-colors ${attrs.includes(f.label) ? "border-primary bg-primary/10 text-primary" : "border-border hover:bg-muted"}`}>
                      {f.label}
                    </button>
                  ))}
                </div>
                {attrs.length > 0 && (
                  <button onClick={() => setAttrs([])} data-testid="attr-clear" className="mt-2 text-xs font-semibold text-primary hover:underline">Clear refinements</button>
                )}
              </div>
            )}
          </div>
        </aside>

        {/* GRID */}
        <div>
          <div className="flex items-center justify-between mb-5">
            <span className="text-sm text-muted-foreground" data-testid="result-count">{shown.length} products</span>
            <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="sort-select"
              className="h-10 px-3 bg-white border border-border rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-primary/40">
              <option value="">Sort: Relevance</option>
              <option value="price_asc">Price: Low to High</option>
              <option value="price_desc">Price: High to Low</option>
              <option value="name">Name: A–Z</option>
            </select>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 6 }).map((_, i) => <div key={`skeleton-${i}`} className="aspect-[3/4] bg-muted rounded-sm animate-pulse" />)}
            </div>
          ) : shown.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground" data-testid="no-results">
              No products match your filters.
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
              {shown.map((p, i) => <ProductCard key={p.slug} product={p} index={i} />)}
            </div>
          )}
        </div>
      </div>

      {!isSearch && collection && (
        <section className="mt-14 rounded-sm overflow-hidden border border-border" data-testid="trade-cta">
          <div className="steel-texture text-white p-8 sm:p-10 flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
            <div className="max-w-xl">
              <span className="text-overline text-white/70">Trade &amp; bulk orders</span>
              <h2 className="mt-1 text-2xl font-display font-black tracking-tight">Buying for a project or in volume?</h2>
              <p className="mt-2 text-sm text-white/80 leading-relaxed">
                Installers and merchants — get tailored trade pricing and a fast bulk quote on {collection.name.toLowerCase()} and the full Myson range. Free tracked UK delivery on every order.
              </p>
            </div>
            <Link
              to={`/contact?type=trade&subject=${encodeURIComponent(`Trade / Bulk Quote — ${collection.name}`)}&message=${encodeURIComponent(`Hi, I'd like a trade / bulk quote for ${collection.name}. Please include your best trade pricing.\n\nProducts / quantities:\n`)}`}
              data-testid="trade-cta-button"
              className="shrink-0 inline-flex items-center gap-2 bg-white text-primary px-6 py-3 rounded-sm font-semibold hover:bg-white/90 transition-colors">
              Request a trade quote <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
